/**
 * sunnyTheme — the owner-approved "Sunny Light" design-language tokens
 * (2026-08 mockup, exact values). The alternate BRIGHT look for the child app
 * shell, chosen by a parent via `lwa_light_theme_v1` (lightThemeFlag.ts,
 * DEFAULT OFF ⇒ the classic Cosmic Dark design renders byte-identically).
 *
 * v1 scope is SHELL-FIRST: app background, headers, child home (incl. the
 * OnePathView chrome), tab bars, the Library list + series detail, Rewards,
 * child preferences and generic chrome (back links, cards, chips, progress
 * rails). Lesson players / arcade / book-reader interiors / tutor chat keep
 * their current styling under either theme in v1.
 *
 * LEGIBILITY RULE (non-negotiable for a light kids' UI): body text is ALWAYS
 * deep navy ink (#1E2340, ~14:1 on the canvas) — never pastel/low-contrast.
 * The WCAG pins live in web-app/__tests__/wcag-contrast.test.ts (Sunny block).
 */
import { darkPalette } from './tokens';
import { isLightThemeEnabled } from './lightThemeFlag';

/**
 * Sunny Light palette — SAME KEY SHAPE as `darkPalette` so shell components
 * can alias either one (`const C = shellPalette()`), leaving flag-OFF renders
 * byte-identical (shellPalette returns the darkPalette OBJECT itself).
 */
export const sunnyPalette = {
  bg:           '#F7F8FC', // canvas/ground — soft off-white, never pure white
  surface:      '#FFFFFF',
  surfaceHigh:  '#EEF1FB', // chip/elevated tint
  indigo:       '#5B4FE8', // brand accents keep the dark theme's hues
  coral:        '#FF8A6B',
  mint:         '#0E9C8E', // teal reads on light surfaces (dark-mode mint does not)
  gold:         '#FFB020', // sunny gold
  reading:      '#10B981',
  writing:      '#AF52DE',
  border:       '#E9EDF7',
  borderBright: '#D8DEF3',
  text:         '#1E2340', // ink — ALWAYS deep navy
  textSub:      '#5A6180',
  textMuted:    '#9AA0BC',
} as const;

/** Extra Sunny Light tokens beyond the palette shape (exact mockup values). */
export const SUNNY = {
  canvas:           '#F7F8FC',
  surface:          '#FFFFFF',
  border:           '#E9EDF7',
  ink:              '#1E2340',
  inkSub:           '#5A6180',
  inkMuted:         '#9AA0BC',
  indigo:           '#5B4FE8',
  teal:             '#0E9C8E',
  coral:            '#FF8A6B',
  gold:             '#FFB020',
  /** Coral→gold accent gradient. */
  coralGrad:        'linear-gradient(135deg,#FF8A6B,#FFB020)',
  /** Hero/CTA gradient — white ink on it. */
  heroGrad:         'linear-gradient(135deg,#6C63FF,#8B7BFF)',
  heroGradFrom:     '#6C63FF',
  heroGradTo:       '#8B7BFF',
  heroInk:          '#FFFFFF',
  heroProgressFill: '#FFDD8A',
  /** CTA = white pill with deep-indigo ink. */
  ctaBg:            '#FFFFFF',
  ctaInk:           '#453BC9',
  ctaShadow:        '0 6px 16px rgba(76,58,180,0.28)',
  chipBg:           '#EEF1FB',
  chipInk:          '#453BC9',
  achievementBg:    '#FFF6E6',
  achievementBorder:'#FCE3B5',
  /** Track/progress rail. */
  rail:             '#E7EBFA',
  tabBarBg:         '#FFFFFF',
  tabActive:        '#453BC9',
  tabInactive:      '#9AA0BC',
  /** Radius scale — rounder than dark (18px; 24px for hero/large cards). */
  radius:           18,
  radiusLg:         24,
  /** Soft brand-tinted "clay" shadows — NO dark glows. */
  shadowClay:       '0 10px 28px rgba(91,79,232,0.16), 0 2px 5px rgba(20,20,40,0.06)',
  shadowCard:       '0 6px 18px rgba(91,79,232,0.09), 0 1px 2px rgba(20,20,40,0.05)',
} as const;

export type ShellPalette = typeof darkPalette | typeof sunnyPalette;

/**
 * The palette the child shell should render from RIGHT NOW.
 * Flag OFF returns the `darkPalette` OBJECT itself (not a copy) — the
 * byte-identical guarantee the theme-contract test pins. Reads the signed
 * flag on every call; callers re-render via web-child's useSunnyShell().
 */
export function shellPalette(): ShellPalette {
  return isLightThemeEnabled() ? sunnyPalette : darkPalette;
}
